import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { hubBaseUrl, hubHosted, loadHubHeaderScript } from "lib/hubSso";

/**
 * Mounts the embeddable Semi360 Hub header (brand / product switcher / account
 * menu) above the app. Renders nothing when not hub-hosted, so localhost keeps
 * the product's own chrome.
 *
 * Two rules, both learned elsewhere in the suite:
 *  - **Mount once.** Two call sites in Sample360 stacked two headers, one
 *    showing a demo org name. The script tag and the element are both guarded
 *    by id.
 *  - **Attributes, not props.** It is a custom element, so React would
 *    otherwise set unknown DOM properties that the component never reads.
 */
export default function HubHeader() {
  const slot = useRef(null);
  const user = useSelector((state) => state?.user?.user || null);

  const org = user?.company_name || user?.company || "";
  const name = user?.name || user?.email || "";

  useEffect(() => {
    if (!hubHosted()) return;
    loadHubHeaderScript();

    const host = slot.current;
    if (!host || host.firstChild) return; // already mounted — never stack two

    const h = document.createElement("semi360-header");
    h.setAttribute("product", "qbuy360");
    h.setAttribute("base", hubBaseUrl());
    if (org) h.setAttribute("org", org);
    if (name) h.setAttribute("user", name);
    // require-auth: the header does Hub login detection for the whole page — if
    // the session goes anonymous it redirects to {HUB}/signin?next=…
    h.setAttribute("require-auth", "");
    host.appendChild(h);
  }, [org, name]);

  // Keep org/user in step if the profile loads after the first paint —
  // setAttribute on the live element, since the mount above runs only once.
  useEffect(() => {
    const el = slot.current?.firstElementChild;
    if (!el) return;
    if (org) el.setAttribute("org", org);
    if (name) el.setAttribute("user", name);
  }, [org, name]);

  if (!hubHosted()) return null;
  return <div ref={slot} id="s360chrome" />;
}
